import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import authService from '../services/authService';
import './LoginPage.css';

const initialForm = {
  email: '',
  username: '',
  password: '',
  re_password: '',
};

function getErrorMessages(error) {
  const data = error?.response?.data;
  if (!data || typeof data !== 'object') {
    return { form: error?.message || 'Unable to create your account. Please try again.' };
  }

  const fieldErrors = {};
  const formErrors = [];
  Object.entries(data).forEach(([field, messages]) => {
    if (field === 'result' || field === 'detail') return;
    const values = Array.isArray(messages) ? messages : [messages];
    if (field === 'non_field_errors') {
      formErrors.push(...values.filter(Boolean));
      return;
    }
    fieldErrors[field] = values.filter(Boolean).join(' ');
  });

  let formMessage = typeof data.detail === 'string' ? data.detail : formErrors.join(' ');
  if (!formMessage && Object.values(fieldErrors).length === 0) {
    formMessage = 'Unable to create your account. Please try again.';
  }

  return { fieldErrors, form: formMessage };
}

export default function SignupPage() {
  const navigate = useNavigate();
  const [form, setForm] = useState(initialForm);
  const [fieldErrors, setFieldErrors] = useState({});
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState('');

  const updateField = (field) => (event) => {
    setForm((current) => ({ ...current, [field]: event.target.value }));
    setFieldErrors((current) => ({ ...current, [field]: '' }));
    setError('');
  };

  const onSubmit = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    setError('');
    setFieldErrors({});

    try {
      await authService.register(form);
      setRegisteredEmail(form.email);
    } catch (submissionError) {
      const messages = getErrorMessages(submissionError);
      setFieldErrors(messages.fieldErrors || {});
      setError(messages.form || 'Please correct the highlighted fields.');
    } finally {
      setSubmitting(false);
    }
  };

  if (registeredEmail) {
    return (
      <div className="login-page">
        <div className="card login-page__card">
          <div className="login-page__eyebrow">Account created</div>
          <h1 className="login-page__title">Check your email</h1>
          <p className="login-page__description">
            We sent an activation link to <strong>{registeredEmail}</strong>. Activate your account before signing in.
          </p>
          <button type="button" className="login-page__submit" onClick={() => navigate('/login')}>
            Go to sign in
          </button>
          <p className="login-page__links">
            Didn’t receive it? Check your spam folder or contact support.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="login-page">
      <div className="card login-page__card signup-page__card">
        <div className="login-page__eyebrow">ProfitFactor workspace</div>
        <h1 className="login-page__title">Create your account</h1>
        <p className="login-page__description">Set up one workspace for your strategies and accounts.</p>

        <form onSubmit={onSubmit} className="login-page__form">
          <label className="login-page__label">
            Username
            <input
              className="login-page__input"
              type="text"
              value={form.username}
              onChange={updateField('username')}
              autoComplete="username"
              minLength="1"
              required
              aria-invalid={Boolean(fieldErrors.username)}
            />
            {fieldErrors.username ? <span className="login-page__field-error">{fieldErrors.username}</span> : null}
          </label>

          <label className="login-page__label">
            Email
            <input
              className="login-page__input"
              type="email"
              value={form.email}
              onChange={updateField('email')}
              autoComplete="email"
              required
              aria-invalid={Boolean(fieldErrors.email)}
            />
            {fieldErrors.email ? <span className="login-page__field-error">{fieldErrors.email}</span> : null}
          </label>

          <label className="login-page__label">
            Password
            <input
              className="login-page__input"
              type="password"
              value={form.password}
              onChange={updateField('password')}
              autoComplete="new-password"
              required
              aria-invalid={Boolean(fieldErrors.password)}
            />
            {fieldErrors.password ? <span className="login-page__field-error">{fieldErrors.password}</span> : null}
          </label>

          <label className="login-page__label">
            Confirm password
            <input
              className="login-page__input"
              type="password"
              value={form.re_password}
              onChange={updateField('re_password')}
              autoComplete="new-password"
              required
              aria-invalid={Boolean(fieldErrors.re_password)}
            />
            {fieldErrors.re_password ? <span className="login-page__field-error">{fieldErrors.re_password}</span> : null}
          </label>

          {error ? <div className="login-page__error">{error}</div> : null}

          <button type="submit" disabled={submitting} className="login-page__submit">
            {submitting ? 'Creating account…' : 'Create account'}
          </button>
        </form>

        <p className="login-page__links">
          Already have an account? <Link to="/login">Sign in</Link>
        </p>
      </div>
    </div>
  );
}
